import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  axios,
  BookingService,
  Sitter,
  SitterReview,
  SitterReviewService,
  SitterService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const MAX_COMMENT_LENGTH = 2500;
const MIN_COMMENT_LENGTH = 5;

type RelationshipCode = NonNullable<SitterReview['relationship']>;
// Form state extends SitterReview so relationship can be empty-string while
// the user hasn't chosen yet.
interface FormReview extends Omit<SitterReview, 'relationship'> {
  relationship?: RelationshipCode | '';
}

export const SitterRecommend = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const parent = stores.parent.parent;

  const [sitter, setSitter] = useState<Sitter | null>(null);
  const [review, setReview] = useState<FormReview>({
    relationship: '',
    comment: '',
  });
  const [, setIsExisting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const sitterId = pathId ? Number(pathId) : undefined;

  useEffect(() => {
    document.title = 'UrbanSitter - Recommend Caregiver';
  }, []);

  useEffect(() => {
    if (!user?.id || !sitterId) return;
    if (!parent?.id) {
      toast.error(t('pages.sitterRecommend.parentOnlyError'));
      navigate(`/sitter/${sitterId}`);
      return;
    }

    let cancelled = false;

    (async (): Promise<void> => {
      try {
        // Has this parent already recommended this sitter?
        const existing = (
          await SitterReviewService.getByFilter({
            params: {
              parentId: user.id,
              userId: user.id,
              sitterId,
              limit: 1,
            },
          })
        ).data;
        const existingData = (existing.data ?? []) as SitterReview[];

        if (!cancelled && existingData.length > 0) {
          const first = existingData[0];
          setSitter(first.sitter ?? null);
          setReview({
            id: first.id,
            sitterId: first.sitterId,
            parent: first.parent,
            comment: first.comment ?? '',
            relationship: first.relationship ?? '',
            rating: 5,
          });
          setIsExisting(true);
        } else if (!cancelled) {
          const sitterResp = (
            await SitterService.get(sitterId, {
              params: { parentId: user.id, userId: user.id },
            })
          ).data;
          if (!cancelled) {
            setSitter(sitterResp.data ?? null);
            setReview({
              sitterId,
              parent: { id: user.id },
              comment: '',
              relationship: '',
              rating: 5,
            });
            setIsExisting(false);
          }
        }

        // If this parent has completed bookings with this sitter, recommend
        // isn't the right page — redirect them to the full review flow.
        const bookings = (
          await BookingService.getByFilter({
            params: {
              limit: 1,
              sitterId,
              parentId: user.id,
              userId: user.id,
              statuses: ['closed_completed', 'parent_cancel', 'sitter_cancel'],
            },
          })
        ).data;
        const meta = bookings.meta;
        if (!cancelled && meta?.size && meta.size > 0) {
          navigate(`/sitter/${sitterId}/review`, { replace: true });
          return;
        }
      } catch {
        if (!cancelled) {
          navigate(`/sitter/${sitterId}`, { replace: true });
          return;
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return (): void => {
      cancelled = true;
    };
  }, [user?.id, sitterId, parent?.id, navigate, t]);

  const commentText = review.comment ?? '';
  const remaining = MAX_COMMENT_LENGTH - commentText.length - (commentText.match(/\n/g) ?? []).length;
  const canSubmit =
    !submitting &&
    !!review.relationship &&
    (review.comment?.length ?? 0) >= MIN_COMMENT_LENGTH &&
    (review.comment?.length ?? 0) <= MAX_COMMENT_LENGTH;

  const submit = async (): Promise<void> => {
    if (!canSubmit || !sitter?.id || !user?.id || !review.relationship) return;

    // Backend (SitterReviewServiceImpl.getSitterId) reads dto.getSitter().getId()
    // — the flat sitterId field is ignored, so the nested sitter object is required.
    const body: SitterReview = {
      ...review,
      relationship: review.relationship as RelationshipCode,
      sitter: { id: sitter.id },
      sitterId: sitter.id,
      parent: parent ?? { id: user.id },
      visibility: 'public',
      rating: 5,
      ratingPercent: 100,
    };

    setSubmitting(true);
    try {
      if (review.id) {
        await SitterReviewService.patch(review.id, body);
        toast.success(t('pages.sitterRecommend.toast.updated'));
      } else {
        await SitterReviewService.create(body);
        toast.success(t('pages.sitterRecommend.toast.created'));
      }
      navigate(`/sitter/${sitter.id}`);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
        toast.error(e.response.data.errors[0].message);
      } else {
        toast.error(t('pages.sitterRecommend.toast.submitFailed'));
      }
      setSubmitting(false);
    }
  };

  if (loading || !sitter) {
    return (
      <div className='container py-8'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  const relationshipOptions: Array<{ code: RelationshipCode; label: string }> = [
    { code: 'friend', label: t('pages.sitterRecommend.relationships.friend') },
    { code: 'family', label: t('pages.sitterRecommend.relationships.family') },
    { code: 'colleague', label: t('pages.sitterRecommend.relationships.colleague') },
    { code: 'employer', label: t('pages.sitterRecommend.relationships.employer') },
    { code: 'other', label: t('pages.sitterRecommend.relationships.other') },
  ];

  return (
    <div className='container py-8 max-w-3xl'>
      <div className='us-card p-6 rounded'>
        <div className='flex gap-4 items-start mb-4'>
          <a href={`/app/sitter/${sitter.id}`} className='flex-shrink-0'>
            {sitter.pictureUrl && (
              <img
                src={sitter.pictureUrl}
                alt={sitter.firstName ?? ''}
                className='w-20 h-20 rounded-full object-cover'
              />
            )}
          </a>
          <h2 className='text-xl font-semibold'>
            {t('pages.sitterRecommend.heading', { name: sitter.firstName ?? '' })}
          </h2>
        </div>
        <hr className='mb-6' />

        <div className='mb-6'>
          <label htmlFor='relationship' className='block text-sm font-medium mb-2'>
            {t('pages.sitterRecommend.relationshipLabel')}
          </label>
          <select
            id='relationship'
            className='form-select border border-neutral-300 rounded px-3 py-2 focus:ring-sapphire focus:border-sapphire'
            value={review.relationship ?? ''}
            onChange={(e): void => setReview((r) => ({
              ...r,
              relationship: e.target.value as RelationshipCode,
            }))}
          >
            <option value=''>{t('pages.sitterRecommend.selectPlaceholder')}</option>
            {relationshipOptions.map((opt) => (
              <option key={opt.code} value={opt.code}>{opt.label}</option>
            ))}
          </select>
        </div>

        <h4 className='text-base font-semibold mb-2'>{t('pages.sitterRecommend.writeLabel')}</h4>
        <p className='italic text-sm text-neutral-700 mb-2'>
          {t('pages.sitterRecommend.writeGuidanceBefore')}{' '}
          <a href='/review-guidelines' target='_blank' rel='noopener noreferrer' className='underline'>
            {t('pages.sitterRecommend.reviewGuidelinesLink')}
          </a>
        </p>

        <div className='mb-4'>
          <div className='text-right text-xs text-neutral-500 mb-1'>
            {t('pages.sitterRecommend.charactersRemaining', { count: remaining })}
          </div>
          <textarea
            aria-label={t('pages.sitterRecommend.textareaAriaLabel')}
            className='w-full border border-neutral-300 rounded p-2 focus:ring-sapphire focus:border-sapphire'
            rows={6}
            maxLength={MAX_COMMENT_LENGTH}
            value={review.comment ?? ''}
            onChange={(e): void => setReview((r) => ({ ...r, comment: e.target.value }))}
          />
        </div>

        <Button
          buttonType={ButtonType.primary}
          disabled={!canSubmit}
          onClick={submit}
        >
          {t('pages.sitterRecommend.buttonSubmit')}
        </Button>
      </div>
    </div>
  );
});
